import {
  getReadonlyListItemCurrency,
  getReadonlyListItemDate,
  getReadonlyListItemId,
  getReadonlyListItemPill,
  getReadonlyListItemString,
  getReadonlyListItemTranslatedString,
} from '@interxfi/app-components/components/readonly-list/util';
import type { ReadonlyListItemRecord } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import { defaultValueStackedTypographyProps } from '@interxfi/design-system/components/list/readonly/item/value';
import type {
  IxfAccountingV1ManualEntryData,
  IxfAccountingV1ManualEntrySpecDeltas,
  IxfAccountingV1ManualEntryState,
  IxfAccountingV1ManualEntryTransaction,
} from '@interxfi/types/generated/api';
import { getJ5StateMetadataListItems } from '../../j5/state/j5-state-list-items';
import { getAccountingCoaAccountPath } from '../chart-of-accounts/accounting-coa-account-routing';
import { getAccountingTransactionPath } from '../transaction/accounting-transaction-routing';
import { getAccountingManualEntryPath } from './accounting-manual-entry-routing';
import { AccountingManualEntryStatusPill } from './accounting-manual-entry-status-pill';

export function getAccountingManualEntrySpecDeltaListItems(data: IxfAccountingV1ManualEntrySpecDeltas | undefined, prefix = '') {
  return {
    accountId: getReadonlyListItemId(
      `${prefix}accountId`,
      'Account ID',
      data?.accountId,
      getAccountingCoaAccountPath({ kind: 'detail', keys: { accountId: data?.accountId } }),
    ),
    amount: getReadonlyListItemCurrency(`${prefix}amount`, 'Amount', data?.amount),
    note: getReadonlyListItemString(`${prefix}note`, 'Note', data?.note),
  } as const satisfies ReadonlyListItemRecord;
}

export function getAccountingManualEntryDataListItems(data: IxfAccountingV1ManualEntryData | undefined, prefix = '') {
  return {
    transactionId: getReadonlyListItemId(
      `${prefix}transactionId`,
      'Transaction ID',
      data?.transactionId,
      getAccountingTransactionPath({ kind: 'detail', keys: { transactionId: data?.transactionId } }),
    ),
    reversingTransactionId: data?.reversingTransactionId
      ? getReadonlyListItemId(
          `${prefix}reversingTransactionId`,
          'Reversing Transaction ID',
          data?.reversingTransactionId,
          getAccountingTransactionPath({ kind: 'detail', keys: { transactionId: data?.reversingTransactionId } }),
        )
      : undefined,
    date: getReadonlyListItemDate(`${prefix}date`, 'Date', data?.date, 'date_iso'),
    postedTimestamp: getReadonlyListItemDate(`${prefix}postedTimestamp`, 'Posted Timestamp', data?.postedTimestamp, 'timestamp_iso_ms'),
    description: getReadonlyListItemString(`${prefix}description`, 'Description', data?.description),
    autoReverseDate: data?.autoReverseDate
      ? getReadonlyListItemDate(`${prefix}autoReverseDate`, 'Auto-Reverse Date', data.autoReverseDate, 'date_iso')
      : undefined,
    deltas: data?.deltas?.map((delta, index) => getAccountingManualEntrySpecDeltaListItems(delta, `${prefix}deltas.${index}.`)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getAccountingManualEntryStateListItems(data: IxfAccountingV1ManualEntryState | undefined) {
  return {
    metadata: getJ5StateMetadataListItems(data?.metadata),
    entryId: getReadonlyListItemId('entryId', 'Entry ID', data?.entryId),
    status: getReadonlyListItemTranslatedString('status', 'Status', {
      namespace: 'ixfAccountingV1',
      enumName: 'IxfAccountingV1ManualEntryStatus',
      value: data?.status,
    }),
    statusPill: getReadonlyListItemPill('statusPill', 'Status', data?.status ? <AccountingManualEntryStatusPill status={data.status} /> : null),
    data: getAccountingManualEntryDataListItems(data?.data, 'data.'),
  } as const satisfies ReadonlyListItemRecord;
}

export function getAccuntingManualEntryTransactionListItems(data: IxfAccountingV1ManualEntryTransaction | undefined, prefix = '') {
  return {
    entryId: getReadonlyListItemId(
      `${prefix}entryId`,
      'Entry ID',
      data?.entryId,
      getAccountingManualEntryPath({ kind: 'detail', keys: { entryId: data?.entryId } }),
    ),
    description: getReadonlyListItemString(
      `${prefix}description`,
      'Description',
      data?.description,
      undefined,
      data?.description ? { columns: 1, valueTypography: { ...defaultValueStackedTypographyProps, wrap: 'break-word' } } : undefined,
    ),
  } as const satisfies ReadonlyListItemRecord;
}
